import { config } from "../configs";
import Mixpanel from "../utils/mixpanel";
import { getSessionId } from "../utils/session";

const sessionId = getSessionId();

export const tracker = {
  login: {
    formSubmit: (email: string) => {
      Mixpanel.track(`submit:login-credential`, {
        message: `Get login credential form submitted successfully.`,
        email,
      });
    },

    pageView: () => {
      Mixpanel.track(`page:login`, {
        message: `Visited inception login page.`,
        session_id: sessionId,
      });
    },
  },

  verifyAccount: {
    pageView: (uniqueId: string) => {
      Mixpanel.track(`page:verify-account`, {
        message: `Visited verify account page.`,
        uniqueId: uniqueId,
      });
    },

    identify: (zohoCustomerId: string) => {
      Mixpanel.identify(zohoCustomerId);
    },

    formSubmit: () => {
      Mixpanel.track(`submit:verify-pin`, {
        message: `Form submitted to verify pin.`,
      });
    },
  },

  forgotPin: {
    pageView: (uniqueId: string) => {
      Mixpanel.track(`page:forgot-pin`, {
        message: `Visited forgot pin page.`,
        uniqueId: uniqueId,
      });
    },

    formSubmit: () => {
      Mixpanel.track(`submit:forgot-pin`, {
        message: `Forgot pin form submitted.`,
      });
    },
  },

  newClient: {
    userCreated: (email: string) => {
      Mixpanel.track(`consult-form-success`, {
        message: `New user created.`,
        email: email || null,
      });
    },

    formAbandoned: () => {
      Mixpanel.track(`form-abandoned`, {
        message: `User left new client page without filling the form.`,
      });
    },

    formError: () => {
      Mixpanel.track(`form-error`, {
        message: `Form validation error in create new client form.`,
      });
    },

    formSubmit: (email?: string) => {
      Mixpanel.track(`submit:new-client`, {
        message: `Form submitted to create new user.`,
        email: email || null,
      });
    },

    formStarted: () => {
      Mixpanel.track(`form-started`, {
        message: `User started filling the create user form.`,
      });
    },

    pageView: () => {
      Mixpanel.track(`page:new-client`, {
        message: `Visited new client page.`,
      });
    },
  },

  steps: {
    nda: {
      pageView: () => {
        Mixpanel.track(`page:nda`, {
          message: `Visited NDA page.`,
        });
      },

      agree: () => {
        Mixpanel.track(`click:nda-agree`, {
          message: `NDA accepted.`,
        });
      },

      disagree: () => {
        Mixpanel.track(`click:nda-disagree`, {
          message: `NDA rejected.`,
        });
      },
      // Track when the NDA checkbox is clicked
      checkbox: () => {
        Mixpanel.track(`click:nda-checkbox`, {
          message: `NDA checkbox clicked.`,
        });
      },
    },

    video: {
      pageView: () => {
        Mixpanel.track(`page:explainer-video`, {
          message: `Visited explainer video page.`,
        });
      },

      start: () => {
        Mixpanel.track(`page:explainer-started`, {
          message: `Explainer video started.`,
        });
      },

      complete: () => {
        Mixpanel.track(`page:explainer-finished`, {
          message: `Explainer video completed.`,
        });
      },
    },

    presentation: {
      pageView: () => {
        Mixpanel.track(`page:presentation-started`, {
          message: `Presentation page view/started.`,
        });
      },

      start: () => {
        Mixpanel.track(`page:presentation-started`, {
          message: `Presentation started.`,
        });
      },

      complete: () => {
        Mixpanel.track(`page:presentation-finished`, {
          message: `Presentation completed.`,
        });
      },

      step: (step: number | string) => {
        Mixpanel.track(`click:presentation-step ${step}`, {
          message: `Presentation step ${step} viewed.`,
          step,
        });
      },
    },
  },

  dashboard: {
    navbar: {
      onClick: (navItem: string) => {
        Mixpanel.track(`click:navbar`, {
          message: `Clicked on ${navItem} button.`,
        });
      },
    },

    logout: {
      openModal: () => {
        Mixpanel.track(`modal:logout`, {
          message: `Open log out modal.`,
          session_id: sessionId,
        });
      },

      closeModal: () => {
        Mixpanel.track(`modal:logout`, {
          message: `Close log out modal.`,
          session_id: sessionId,
        });
      },

      logout: () => {
        Mixpanel.track(`logout`, {
          message: `User logged out.`,
        });
      },
    },

    cpa: {
      onClick: (isUpdate: boolean) => {
        isUpdate
          ? Mixpanel.track(`click:update-cpa`, {
            message: `Clicked on update cpa button.`,
          })
          : Mixpanel.track(`click:add-cpa`, {
            message: `Clicked on add cpa button.`,
          });
      },

      closeModal: () => {
        Mixpanel.track(`modal:cpa`, {
          message: `Close cpa modal.`,
        });
      },

      formSubmit: (isUpdate: boolean) => {
        isUpdate
          ? Mixpanel.track(`submit:update-cpa`, {
            message: `Update cpa form submitted.`,
          })
          : Mixpanel.track(`submit:add-cpa`, {
            message: `Add cpa form submitted.`,
          });
      },
    },

    advisor: {
      onClick: (isUpdate: boolean) => {
        isUpdate
          ? Mixpanel.track(`click:update-advisor`, {
            message: `Clicked on update advisor button.`,
          })
          : Mixpanel.track(`click:add-advisor`, {
            message: `Clicked on add advisor button.`,
          });
      },

      closeModal: () => {
        Mixpanel.track(`modal:advisor`, {
          message: `Close advisor modal.`,
        });
      },

      formSubmit: (isUpdate: boolean) => {
        isUpdate
          ? Mixpanel.track(`submit:update-advisor`, {
            message: `Update advisor form submitted.`,
          })
          : Mixpanel.track(`submit:add-advisor`, {
            message: `Add advisor form submitted.`,
          });
      },
    },

    bookkeeper: {
      onClick: (isUpdate: boolean) => {
        isUpdate
          ? Mixpanel.track(`click:update-bookkeeper`, {
            message: `Clicked on update bookkeeper button.`,
          })
          : Mixpanel.track(`click:add-bookkeeper`, {
            message: `Clicked on add bookkeeper button.`,
          });
      },

      closeModal: () => {
        Mixpanel.track(`modal:bookkeeper`, {
          message: `Close bookkeeper modal.`,
        });
      },

      formSubmit: (isUpdate: boolean) => {
        isUpdate
          ? Mixpanel.track(`submit:update-bookkeeper`, {
            message: `Update bookkeeper form submitted.`,
          })
          : Mixpanel.track(`submit:add-bookkeeper`, {
            message: `Add bookkeeper form submitted.`,
          });
      },
    },

    overview: {

      //ONE PAGE OVERVIEW & CLIENT PRESENTATION PPT
      pdfModal: {
        onOpen: (name: string) => {
          const folderName = toKebabCase(name);
          Mixpanel.track(`click:${folderName}`, {
            message: `Opened ${name.toUpperCase()} modal in overview.`,
            module: "Overview"
          });
        },

        onClose: () => {
          Mixpanel.track(`click:close`, {
            message: `Closed pdf modal in overview.`,
            module: "Overview"
          });
        },
      },

      //ONE PAGE OVERVIEW & CLIENT PRESENTATION PPT
      videoModal: {
        onOpen: (name: string) => {
          const folderName = toKebabCase(name);
          Mixpanel.track(`click:${folderName}`, {
            message: `Opened ${name.toUpperCase()} modal in overview.`,
            module: "Overview"
          });
        },

        onClose: () => {
          Mixpanel.track(`click:close`, {
            message: `Closed video modal in overview.`,
            module: "Overview"
          });
        },
      },

      dynamicItem: {
        onClick: (title: string) => {
          const formattedTitle = toKebabCase(title);
          Mixpanel.track(`click:${formattedTitle}`, {
            message: `Clicked on ${title.toUpperCase()} in overview.`,
            module: "Overview"
          });
        }
      }
    },

    //overview & my documents
    documentModal: {
      onOpen: (name: string) => {
        const folderName = toKebabCase(name);
        Mixpanel.track(`click:${folderName}`, {
          message: `Opened ${name.toUpperCase()} modal.`,
          module: "Overview"
        });
      },

      onClose: () => {
        Mixpanel.track(`click:close`, {
          message: `Closed document modal.`,
          module: "Overview"
        });
      },
      documentViewed: (name: string) => {
        Mixpanel.track(`view:document-in-overview`, {
          file: name,
          message: `Viewed document in Overview.`,
          module: "Overview"
        });
      },
      documentDownloaded: (name: string) => {
        Mixpanel.track(`download:document-in-overview`, {
          file: name,
          message: `Downloaded document in Overview.`,
          module: "Overview"
        });

      }
    },

    others: {
      uploadProfilePicture: () => {
        Mixpanel.track(`click:add-profile-picture`, {
          message: `Clicked on add profile picture button.`,
        });
      },

      singlePortalFeedback: () => {
        Mixpanel.track(`click:single-portal-feedback`, {
          message: `Clicked on Share Portal Feedback.`,
        });
      },

      solarStudyTracker: () => {
        Mixpanel.track(`click:solar-study-tracker`, {
          message: `Redirected to solar study.`,
        });
      },

      dashboardViewed: () => {
        Mixpanel.track(`page:dashboard`, {
          message: `Visited dashboard.`,
        });
      },
    },

    inviteOthers: {
      formStarted: () => {
        Mixpanel.track(`form:invite-others-started`, {
          message: `Started filling invite others form.`,
          module: "Invite Others"
        });
      },

      formSubmitted: () => {
        Mixpanel.track(`form:invite-others-submitted`, {
          message: `Submitted invite others form.`,
          module: "Invite Others"
        });
      },

      formError: () => {
        Mixpanel.track(`form-error:invite-others`, {
          message: `Form validation error in create new client form.`,
          module: "Invite Others"
        });
      },

    }
    ,
    equityContribution: {
      formStarted: () => {
        Mixpanel.track(`form:equity-contribution-started`, {
          message: `Started filling equity contribution form.`,
          module: "Equity Contribution"
        });
      },

      formSubmitted: () => {
        Mixpanel.track(`form:equity-contribution-submitted`, {
          message: `Submitted equity contribution form.`,
          module: "Equity Contribution"
        });
      },

      formError: () => {
        Mixpanel.track(`form-error:equity-contribution`, {
          message: `Form validation error in equity contribution form.`,
          module: "Equity Contribution"
        });
      },

      noGoogleClick: () => {
        Mixpanel.track(`click:purl-llc-google-no-form`, {
          message: `No Google Form equity contribution form.`,
          module: "Equity Contribution"
        });
      },

      FAQButton: () => {
        Mixpanel.track(`click:purl-llc-faq-button`, {
          message: `FAQ Form equity contribution form.`,
          module: "Equity Contribution"
        });
      }

    },
    equityContributionNew: {
      formStarted: () => {
        Mixpanel.track(`form:equity-contribution-new-llc-started`, {
          message: `Started filling equity contribution new llc form.`,
        });
      },

      formSubmitted: () => {
        Mixpanel.track(`form:equity-contribution-new-llc-submitted`, {
          message: `Submitted equity contribution new llc form.`,
        });
      },

      formError: () => {
        Mixpanel.track(`form-error:equity-contribution-new-llc`, {
          message: `Form validation error in equity contribution new llc form.`,
        });
      },
      formView: () => {
        Mixpanel.track(`view:equity-contribution-new-llc`, {
          message: `View equity contribution new llc form.`,
        });
      },
      formAbandoned: () => {
        Mixpanel.track(`form-abandoned:equity-contribution-new-llc`, {
          message: `Abandoned equity contribution new llc form.`,
        });
      }

    },
    equityContributionExisting: {
      formStarted: () => {
        Mixpanel.track(`form:equity-contribution-existing-llc-started`, {
          message: `Started filling equity contribution existing llc form.`,
        });
      },

      formSubmitted: () => {
        Mixpanel.track(`form:equity-contribution-existing-llc-submitted`, {
          message: `Submitted equity contribution existing llc form.`,
        });
      },

      formError: () => {
        Mixpanel.track(`form-error:equity-contribution-existing-llc`, {
          message: `Form validation error in equity contribution existing llc form.`,
        });
      },
      formView: () => {
        Mixpanel.track(`view:equity-contribution-existing-llc`, {
          message: `View equity contribution existing llc form.`,
        });
      },
      formAbandoned: () => {
        Mixpanel.track(`form-abandoned:equity-contribution-existing-llc`, {
          message: `Abandoned equity contribution existing llc form.`,
        });
      },

    },

    additionalResources: {
      knowledgeCenter: {
        onClick: () => {
          Mixpanel.track(`click:knowledge-center`, {
            message: `Clicked on KNOWLEDGE CENTER.`,
            module: "Additional Resources"
          });
        },
      },
      solarStudy: {
        onClick: () => {
          Mixpanel.track(`click:solar-study`, {
            message: `Clicked on SOLAR STUDY in additional resources.`,
            module: "Additional Resources"
          });
        },
      },
      materialParticipationOverview: {
        onClick: () => {
          Mixpanel.track(`click:material-participation-overview`, {
            message: `Clicked on Material Participation Overview in additional resources.`,
            module: "Additional Resources"
          });
        },
      },
      mathExplainerOverview: {
        onClick: () => {
          Mixpanel.track(`click:asset-ownership-math`, {
            message: `Clicked on Asset Ownership: The Math  in additional resources.`,
            module: "Additional Resources"
          });
        },
      },
      dynamicItem: {
        onClick: (title: string) => {
          const formattedTitle = toKebabCase(title);
          Mixpanel.track(`click:${formattedTitle}`, {
            message: `Clicked on ${title.toUpperCase()} in additional resources.`,
            module: "Additional Resources"
          });
        }
      }
    },

    scheduleACallWithCPA: {
      scheduleACall: {
        onClick: () => {
          Mixpanel.track(`click:purl-schedule-a-call`, {
            message: `Clicked on Schedule a Call.`,
            module: "Next Step: Schedule a Call with CPA",
            calendlyUrl: `${config.REACT_APP_CALENDLY_URL}.`
          });
        },
      },
      onClose: () => {
        Mixpanel.track(`click:purl-close-schedule-a-call`, {
          message: `Closed Schedule a Call modal.`,
          module: "Next Step: Schedule a Call with CPA"
        });
      },
      joinMeeting: {
        onClick: (type: string, url: string) => {
          Mixpanel.track(`click:purl-join-meeting`, {
            message: `Clicked on Join Meeting (${type}).`,
            module: "Next Step: Schedule a Call with CPA",
            platform: type,
            url: url
          });
        }
      },
      calendlyWidget: {
        onView: (url: string) => {
          Mixpanel.track(`view:purl-calendly-widget`, {
            message: `Calendly inline widget displayed.`,
            module: "Next Step: Schedule a Call with CPA",
            url: url
          });
        }
      },
    }


  },

  scroll: {
    scrollStart: (name: string, threshold: string, direction: string) => {
      Mixpanel.track(`scroll: ${name}`, {
        message: `User scrolled to ${threshold} ${direction} in ${name}.`,
      });
    },
  }
  ,
  document: {
    documentViewed: (name: string, location: string) => {
      Mixpanel.track(`view-document:${name}`, {
        message: `User viewed ${name} from ${location}.`,
        module: `${location}`
      });
    },
    documentDownload: (name: string, location: string) => {
      Mixpanel.track(`download-document:${name}`, {
        message: `User downloaded ${name} from ${location}.`,
        module: `${location}`
      });
    }
  }
  ,
  proforma: {

    identify: (proformaUserId: string) => {
      Mixpanel.identify(proformaUserId);
    },
    formStarted: () => {
      Mixpanel.track(`proforma:form-started`, {
        message: `User started filling the Proforma form.`,
      });
    },
    formError: (username: string) => {
      Mixpanel.track(`proforma:form-error`, {
        message: `Form validation error in Proforma form.`,
        username: username,
      });
    },
    loginForm: (username: string) => {
      Mixpanel.track(`proforma:login-error`, {
        message: `Error in login form.`,
        username: username,
      });
    },
    loginFailed: (username: string, message: string) => {
      Mixpanel.track(`proforma:login-failed`, {
        message: message,
        username: username,
      });
    },
    formSubmit: (id: string) => {
      Mixpanel.track(`submit:proforma-login`, {
        message: `Get login credential form submitted successfully.`,
        uniqueId: id,
      });
    },

    pageView: () => {
      Mixpanel.track(`page:proforma-login-view`, {
        message: `Visited inception proforma login page.`,
      });
    }
    ,
    proformaFormView: (username: string) => {
      Mixpanel.track(`view:proforma-form`, {
        message: `Viewed inception proforma form.`,
        username: username
      })
    }
    ,
    finalizeProforma: (username: string) => {
      Mixpanel.track(`click:finalize-proforma`, {
        message: `Clicked on finalize proforma button.`,
        username: username
      });
    }
    ,
    logout: (username: string) => {
      Mixpanel.track(`logout:proforma`, {
        message: `User logged out.`,
        username: username
      });
    },
    finalizeProformaFormStarted: () => {
      Mixpanel.track(`finalize-proforma:form-started`, {
        message: `User started filling the finalize proforma form.`,
      });
    },
    finalizeProformaFormSubmitted: () => {
      Mixpanel.track(`finalize-proforma:form-submitted`, {
        message: `User submitted the finalize proforma form.`,
      });
    },
    finalizeProformaSuccess: () => {
      Mixpanel.track(`finalize-proforma:success`, {
        message: `Existing proforma finalized successfully.`,
      });
    },
    finalizeProformaFormError: (username: string) => {
      Mixpanel.track(`finalize-proforma:form-error`, {
        message: `Validation error in finalize proforma form.`,
        username: username,
      });
    },
    finalizeProformaFailed: (username: string, message: string) => {
      Mixpanel.track(`finalize-proforma:failed`, {
        message: message,
        username: username,
      });
    },
    ampProformaSubmit: (username: string) => {
      Mixpanel.track(`proforma-amp:submit`, {
        message: `AmpVantage Energy Proforma submitted.`,
        username: username
      });
    },
    regularProformaSubmit: (username: string) => {
      Mixpanel.track(`proforma:submit`, {
        message: `Proforma submitted.`,
        username: username
      });
    },
  },
  chatbot: {
    appeared: () => {
      Mixpanel.track(`view:chatbot`, {
        message: `Chatbot floating action button appeared after page load.`,
        module: "Chatbot",
      });
    },

    opened: () => {
      Mixpanel.track(`click:chatbot-open`, {
        message: `User clicked the chatbot floating action button to open it.`,
        module: "Chatbot",
      });
    },

    closed: (timeSpentSeconds: number) => {
      Mixpanel.track(`click:chatbot-close`, {
        message: `User closed the chatbot floating action button. Total time spent chatting: ${timeSpentSeconds} seconds.`,
        module: "Chatbot",
        time_spent_seconds: timeSpentSeconds,
      });
    },
  },

  entityFormation: {

    login: {
      pageView: () => {
        Mixpanel.track(`page:entity-formation`, {
          message: `Visited Login entity formation page.`,
        });
      },
      formStarted: () => {
        Mixpanel.track(`form:entity-formation-started`, {
          message: `Started filling Login entity formation form.`,
        });
      },
      formSubmitted: () => {
        Mixpanel.track(`form:entity-formation-submitted`, {
          message: `Submitted Login entity formation form.`,
        });
      }
    },
    table: {
      pageView: (uniqueId: string) => {
        Mixpanel.track(`page:entity-formation-table`, {
          message: `Visited entity formation table page.`,
          uniqueId: uniqueId
        });
      },

    }
  },
};



const toKebabCase = (input: string): string => {
  return input.trim().toLowerCase().replace(/\s+/g, "-");
};
